
import * as THREE from '/assets/scripts/three.module.js';
import { OrbitControls } from '/assets/scripts/OrbitControls.js';
import { loadCatalogBundle } from './data/catalog.js?v=20260817-relbridge9';
import { createNearFitController } from './simulator/near-fit.js?v=20260820-perf2';
import { createSimulatorWorkbenchController } from './simulator/workbench-controller.js?v=20260820-perf2';
import { createWorkbenchRuntime } from './runtime/workbench-runtime.js?v=20260820-perf11';
import { createCatalogSearchUi, filterCaseDatasetByVolume, bindCaseVolumeSearchFilters, displayLastSyncTime } from './ui/catalog-search.js?v=20260817-catalog1';
import { initMobileDrawerEngine } from './ui/mobile-drawer.js?v=20260816-mainref1';
import { createEnvironmentController } from './scene/environment.js?v=20260816-mainref1';
import { createManifestUi } from './ui/manifest.js?v=20260817-boardfan3';
import { createCaseNavigation, slugifyUrlSegment, caseRouteSlug, normalizePresetName, formatCaseBrandTitle } from './ui/case-navigation.js?v=20260822-routeexit1';
import { createConflictVisualizer } from './runtime/conflict-visualizer.js?v=20260817-relbridge9';
import { createObjectInteractionController } from './runtime/object-interactions.js?v=20260817-relbridge9';
import { decodeSffBuildToken, encodeSffBuildToken, parse3dBuildRoute, databaseBridgePath } from './bridge/build-state.js?v=20260818-bridge5';

window.THREE = THREE;

let scene, camera, renderer, controls;
let raycaster, mouse;
let ambientLight, hemiLight, sunLight, headLight;
let viewport = null;

const activeBoxes = [];
let nearFitController = null;
let simulatorController = null;
let workbenchRuntime = null;
let environmentController = null;
let manifestUi = null;
let conflictVisualizer = null;
let interactionController = null;
let caseNavigation = null;

let CASES_DATASET = [];
let GPU_DATASET = [];
let COOLER_DATASET = [];
let RAM_DATASET = [];
let AIO_DATASET = [];
let MOTHERBOARD_DATASET = [];
let PSU_DATASET = [];
let STRUCTURED_INDEX = { byName: new Map(), byKey: new Map(), count: 0 };
let CATALOG_DIAGNOSTICS = {};
let CASE_INDEX_BY_SLUG = new Map();
let CASE_INDEX_BY_NORMALIZED_NAME = new Map();
let CATALOG_BY_ID = new Map();
let AMBIGUOUS_CATALOG_IDS = new Set();

const catalogSearchUi = createCatalogSearchUi({
  formatCaseBrandTitle,
  hasActiveCase: () => !!simulatorController?.activeCaseBox?.()
});
const { buildUnifiedHardwareLabel, initDynamicSearchCombobox } = catalogSearchUi;

initMobileDrawerEngine();

function bindStaticSidebarControls() {
  const requiredIds=[
    'ramSearchCombobox','custom-ram-height','custom-ram-status',
    'custom-fan-size','custom-fan-thick','custom-fan-placement','custom-fan-status'
  ];
  const missing=requiredIds.filter(id=>!document.getElementById(id));
  if(missing.length){
    console.warn('[SFF 3D] Static sidebar controls missing from page template:',missing);
  }

  const size=document.getElementById('custom-fan-size');
  const thickness=document.getElementById('custom-fan-thick');
  const placement=document.getElementById('custom-fan-placement');
  const refresh=()=>simulatorController?.refreshFanPlacementUi?.();
  size?.addEventListener('change',refresh);
  thickness?.addEventListener('change',refresh);
  placement?.addEventListener('focus',refresh);
  placement?.addEventListener('pointerdown',refresh);
  refresh();
}

function scheduleInitialEnvironment() {
  const renderEnvironment = () => environmentController?.renderDeskSurfaceEnvironment?.();
  const afterFirstPaint = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(renderEnvironment, { timeout: 900 });
    } else {
      setTimeout(renderEnvironment, 180);
    }
  };
  requestAnimationFrame(() => requestAnimationFrame(afterFirstPaint));
}

window.toggleThemeMode = () => environmentController?.toggleThemeMode?.();
window.updateMobileManifestCountBadge = () => manifestUi?.updateMobileManifestCountBadge?.();
window.handleDeskEnvironmentChange = value => environmentController?.handleDeskEnvironmentChange?.(value);

setTimeout(initEngine, 50);

function initEngine() {
  viewport = document.getElementById('sff-canvas-viewport');
  if (!viewport) { setTimeout(initEngine, 200); return; }

  try {
    viewport.style.position = 'relative';
    viewport.style.display = 'block';

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0x050507);
    scene.fog = new THREE.FogExp2(0x050507, 0.0003);

    const initW = viewport.clientWidth > 0 ? viewport.clientWidth : 800;
    const initH = viewport.clientHeight > 0 ? viewport.clientHeight : 550;

    camera = new THREE.PerspectiveCamera(45, initW / initH, 10, 15000);
    camera.position.set(0, 850, 1500);

    renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setSize(initW, initH, true);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = true;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.5;
    viewport.appendChild(renderer.domElement);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.maxPolarAngle = Math.PI / 2 - 0.03;
    controls.addEventListener?.('change', () => workbenchRuntime?.markCameraDirty?.());

    raycaster = new THREE.Raycaster();
    mouse = new THREE.Vector2();

    ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
    scene.add(ambientLight);
    hemiLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.8);
    scene.add(hemiLight);
    sunLight = new THREE.DirectionalLight(0xffffff, 1.5);
    sunLight.position.set(400, 1000, 600);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.width = 1024;
    sunLight.shadow.mapSize.height = 1024;
    scene.add(sunLight);
    headLight = new THREE.DirectionalLight(0xffffff, 1.2);
    scene.add(headLight);

    workbenchRuntime = createWorkbenchRuntime({
      getScene: () => scene,
      getCamera: () => camera,
      getRenderer: () => renderer,
      getControls: () => controls,
      getViewport: () => viewport,
      getHeadLight: () => headLight,
      getActiveBoxes: () => activeBoxes,
      onSpatialVisuals: () => {
        conflictVisualizer?.run?.();
        activeBoxes.forEach(box => box.wireframeHelper?.update?.());
      },
      onBadges: () => manifestUi?.updateFloatingBadges?.(),
      getExtraStats: () => ({ nearFit: nearFitController?.getStats?.() || null })
    });

    manifestUi = createManifestUi({
      getActiveBoxes: () => activeBoxes,
      getViewport: () => viewport,
      getCamera: () => camera,
      buildCaseSpatialControls: box => simulatorController?.buildCaseSpatialControls?.(box) || ''
    });

    environmentController = createEnvironmentController({
      getScene: () => scene,
      getLights: () => ({ ambientLight, hemiLight, sunLight, headLight }),
      disposeObject3DResources: root => workbenchRuntime.disposeObject3DResources(root),
      markRenderDirty: () => workbenchRuntime.markRenderDirty(),
      markShadowDirty: () => workbenchRuntime.markShadowDirty()
    });

    conflictVisualizer = createConflictVisualizer({
      getActiveBoxes: () => activeBoxes,
      getLayoutFamily: () => simulatorController?.getActivePlan?.()?.layoutFamily || null,
      now: () => workbenchRuntime.now(),
      recordPass: ms => workbenchRuntime.recordConflict(ms)
    });

    simulatorController = createSimulatorWorkbenchController({
      activeBoxes,
      scene,
      controls,
      workbenchRuntime,
      manifestUi,
      buildUnifiedHardwareLabel,
      getNearFitController: () => nearFitController,
      getInteractionController: () => interactionController,
      getCasesDataset: () => CASES_DATASET,
      getStructuredIndex: () => STRUCTURED_INDEX,
      getCaseIndexBySlug: () => CASE_INDEX_BY_SLUG,
      getCaseIndexByNormalizedName: () => CASE_INDEX_BY_NORMALIZED_NAME,
      getCatalogDiagnostics: () => CATALOG_DIAGNOSTICS
    });

    interactionController = createObjectInteractionController({
      getActiveBoxes: () => activeBoxes,
      getActiveCase: () => simulatorController.activeCaseBox(),
      getCamera: () => camera,
      getRenderer: () => renderer,
      getControls: () => controls,
      getRaycaster: () => raycaster,
      getMouse: () => mouse,
      getEngineMode: () => simulatorController.getEngineMode(),
      proposePositionAxis: (...args) => simulatorController.proposePositionAxis(...args),
      checkAndAttachToMotherboardSocket: comp => simulatorController.checkAndAttachToMotherboardSocket(comp),
      checkAndSnapFanToRadiator: comp => simulatorController.checkAndSnapFanToRadiator(comp),
      updateAttachedMoboChildren: comp => simulatorController.updateAttachedMoboChildren(comp),
      realignAllComponents: () => simulatorController.realignAllComponentsToActiveBlueprint(),
      markRenderDirty: () => workbenchRuntime.markRenderDirty(),
      markSpatialDirty: () => workbenchRuntime.markSpatialDirty()
    });

    window.stepComponentAxis = (...args) => interactionController.stepComponentAxis(...args);
    window.toggleFeetHeightSelector = (...args) => interactionController.toggleFeetHeightSelector(...args);
    window.adjustCameraTarget = (...args) => interactionController.adjustCameraTarget(...args);
    window.toggleXRayMode = (...args) => interactionController.toggleXRayMode(...args);
    window.adjustComponentX = (...args) => interactionController.adjustComponentX(...args);
    window.adjustComponentHeight = (...args) => interactionController.adjustComponentHeight(...args);
    window.adjustComponentZ = (...args) => interactionController.adjustComponentZ(...args);
    window.flipCaseOrientation = (...args) => interactionController.flipCaseOrientation(...args);
    window.get3DPerformanceStats = () => workbenchRuntime.getStats();

    caseNavigation = createCaseNavigation({
      getCases: () => CASES_DATASET,
      getCaseBySlug: slug => CASE_INDEX_BY_SLUG.get(slug) || null,
      getCaseByNormalizedName: name => CASE_INDEX_BY_NORMALIZED_NAME.get(name) || null,
      spawnCase: (name, dimensions, record) => simulatorController.triggerCaseSpawn(name, dimensions, record)
    });
    window.addCaseToMat = caseName => caseNavigation.addCaseToMat(caseName);

    interactionController.bindRendererEvents();
    loadGlobalPlatformDatabases();
    bindStaticSidebarControls();

    initDynamicSearchCombobox('caseSearchCombobox', () => filterCaseDatasetByVolume(CASES_DATASET), selectedCase => {
      // User case selection leaves any imported schema-2 build route and takes ownership of
      // a clean canonical /3d/<case>/ path. Route synchronization is centralized in navigation.
      caseNavigation.addCaseToMat(selectedCase.name);
    });
    bindCaseVolumeSearchFilters();

    initDynamicSearchCombobox('moboSearchCombobox', () => MOTHERBOARD_DATASET,
      (row, label) => simulatorController.loadCatalogHardwareRecord('motherboard', row, label),
      row => simulatorController.hardwareDatasetEligibility('motherboard', row));
    initDynamicSearchCombobox('psuSearchCombobox', () => PSU_DATASET,
      (row, label) => simulatorController.loadCatalogHardwareRecord('psu', row, label),
      row => simulatorController.hardwareDatasetEligibility('psu', row));
    initDynamicSearchCombobox('gpuSearchCombobox', () => GPU_DATASET,
      (row, label) => simulatorController.loadCatalogHardwareRecord('gpu', row, label),
      row => simulatorController.hardwareDatasetEligibility('gpu', row));
    initDynamicSearchCombobox('coolerSearchCombobox', () => COOLER_DATASET,
      (row, label) => simulatorController.loadCatalogHardwareRecord('aircooler', row, label),
      row => simulatorController.hardwareDatasetEligibility('aircooler', row));
    initDynamicSearchCombobox('ramSearchCombobox', () => RAM_DATASET,
      (row, label) => simulatorController.loadCatalogHardwareRecord('ram', row, label),
      row => simulatorController.hardwareDatasetEligibility('ram', row));
    initDynamicSearchCombobox('aioSearchCombobox', () => AIO_DATASET,
      (row, label) => simulatorController.loadCatalogHardwareRecord('radiator', row, label),
      row => simulatorController.hardwareDatasetEligibility('radiator', row));

    nearFitController = createNearFitController({
      root: document.getElementById('sff-near-fit-panel'),
      getContext: () => ({
        caseBox: simulatorController.activeCaseBox(),
        plan: simulatorController.getActivePlan() || simulatorController.activeCaseBox()?.spatialPlan || null,
        activeBoxes
      }),
      getDatasets: () => ({
        gpus:GPU_DATASET, coolers:COOLER_DATASET, aios:AIO_DATASET,
        motherboards:MOTHERBOARD_DATASET, psus:PSU_DATASET
      }),
      labelFor: buildUnifiedHardwareLabel,
      onChoose: (trackType, row, label) => simulatorController.loadCatalogHardwareRecord(trackType, row, label)
    });
    nearFitController?.setMode?.(simulatorController.getEngineMode());
    if (GPU_DATASET.length || COOLER_DATASET.length || AIO_DATASET.length || MOTHERBOARD_DATASET.length || PSU_DATASET.length) {
      nearFitController?.notifyCatalogReady?.();
    }

    manifestUi.checkEmptyState?.();
    caseNavigation.bindPopularPresetButtons?.();
    simulatorController.bindClearWorkspaceButton();
    workbenchRuntime.installResizeHandling();
    workbenchRuntime.startRenderLoop();
    scheduleInitialEnvironment();
  } catch (err) {
    console.error(err);
  }
}

async function loadGlobalPlatformDatabases() {
  const startedAt = workbenchRuntime?.now?.() ?? Date.now();
  try {
    const bundle = await loadCatalogBundle();
    CASES_DATASET = bundle.cases || [];
    GPU_DATASET = bundle.gpus || [];
    COOLER_DATASET = bundle.coolers || [];
    RAM_DATASET = bundle.rams || [];
    AIO_DATASET = bundle.aios || [];
    MOTHERBOARD_DATASET = bundle.motherboards || [];
    PSU_DATASET = bundle.psus || [];
    STRUCTURED_INDEX = bundle.structuredIndex || STRUCTURED_INDEX;
    CATALOG_DIAGNOSTICS = bundle.diagnostics || {};
    displayLastSyncTime(bundle.meta);

    CASE_INDEX_BY_SLUG = new Map();
    CASE_INDEX_BY_NORMALIZED_NAME = new Map();
    for (const caseRow of CASES_DATASET) {
      if (!caseRow?.name) continue;
      const slug = caseRouteSlug(caseRow);
      const normalized = normalizePresetName(caseRow.name);
      if (!CASE_INDEX_BY_SLUG.has(slug)) CASE_INDEX_BY_SLUG.set(slug, caseRow);
      if (!CASE_INDEX_BY_NORMALIZED_NAME.has(normalized)) CASE_INDEX_BY_NORMALIZED_NAME.set(normalized, caseRow);
    }

    // One universal identity index spans cases and every catalog hardware type. The backend
    // hashes category+brand+model into a stable 96-bit ID; 3D only consumes that ID and never
    // re-hashes records in the browser. This is the bridge authority for future /db -> /3d builds.
    CATALOG_BY_ID = new Map();
    AMBIGUOUS_CATALOG_IDS = new Set();
    const fingerprint=(kind,record)=>`${String(kind||'').toLowerCase()}|${JSON.stringify(Object.entries(record||{}).filter(([key])=>!['id','catalogId','catalog_id'].includes(key)).sort(([a],[b])=>a.localeCompare(b)))}`;
    const register=(kind,records)=>(records||[]).forEach(record=>{
      const id=String(record?.catalogId||record?.id||'').trim().toLowerCase();
      if(!id||AMBIGUOUS_CATALOG_IDS.has(id))return;
      const existing=CATALOG_BY_ID.get(id);
      if(!existing){
        // Backend IDs are authoritative. Avoid sorting/stringifying every record up front;
        // only pay for a full fingerprint when the same ID is actually observed twice.
        CATALOG_BY_ID.set(id,{kind,record,fingerprint:null});
        return;
      }
      const existingFingerprint=existing.fingerprint || fingerprint(existing.kind,existing.record);
      existing.fingerprint=existingFingerprint;
      const nextFingerprint=fingerprint(kind,record);
      if(existingFingerprint===nextFingerprint)return;
      CATALOG_BY_ID.delete(id);
      AMBIGUOUS_CATALOG_IDS.add(id);
    });
    register('case',CASES_DATASET);register('gpu',GPU_DATASET);register('aircooler',COOLER_DATASET);
    register('ram',RAM_DATASET);register('radiator',AIO_DATASET);register('motherboard',MOTHERBOARD_DATASET);register('psu',PSU_DATASET);
    window.resolveSffCatalogId=id=>{
      const key=String(id||'').trim().toLowerCase();
      return AMBIGUOUS_CATALOG_IDS.has(key)?null:(CATALOG_BY_ID.get(key)||null);
    };
    window.getSffCatalogIdentityStats=()=>({schema:bundle?.meta?.catalog_ids?.schema||null,records:CATALOG_BY_ID.size,ambiguous_ids:AMBIGUOUS_CATALOG_IDS.size,ambiguous:[...AMBIGUOUS_CATALOG_IDS],backend:bundle?.meta?.catalog_ids||null});
    window.getSffBuildBridgeToken=()=>encodeSffBuildToken(simulatorController?.getSffBuildBridgePayload?.());
    window.getSffDatabaseBridgeUrl=()=>{
      const payload=simulatorController?.getSffBuildBridgePayload?.();
      if(!payload)return '';
      const activeRecord=simulatorController?.activeCaseBox?.()?.catalogRecord || null;
      const caseTarget=activeRecord || simulatorController?.activeCaseBox?.()?.nativeSpecs?.name || 'build';
      return `${window.location.origin}${databaseBridgePath(caseTarget,payload)}`;
    };
    window.openSffBuildInDatabase=()=>{const url=window.getSffDatabaseBridgeUrl?.();if(url)window.open(url,'_blank');return url||'';};

    workbenchRuntime?.setCatalogLoadMs?.((workbenchRuntime?.now?.() ?? Date.now()) - startedAt);
    console.info('[SFF 3D] catalog', CATALOG_DIAGNOSTICS, {
      gpus:GPU_DATASET.length, coolers:COOLER_DATASET.length, ram:RAM_DATASET.length, aios:AIO_DATASET.length,
      motherboards:MOTHERBOARD_DATASET.length, psus:PSU_DATASET.length, cases:CASES_DATASET.length
    });
    simulatorController?.refreshHardwareEligibilityUi?.();
    nearFitController?.notifyCatalogReady?.();
    // Search controls are bound before the async catalog request completes. Rebuild any open
    // combobox once the real datasets arrive so an early-focus "No records matched" state
    // cannot remain stale (RAM exposed this race first because its control is injected at boot).
    for(const input of document.querySelectorAll('.sff-searchable-combobox .combobox-input')){
      if(input?.closest('.sff-searchable-combobox')?.querySelector('.combobox-dropdown.active')){
        input.dispatchEvent(new Event('input',{bubbles:true}));
      }
    }
  } catch (error) {
    console.error('[SFF 3D]: catalog load failed; spatial simulator remains data-empty rather than using synthetic fallback records.', error);
    CATALOG_DIAGNOSTICS = { ...CATALOG_DIAGNOSTICS, error:String(error?.message || error), failClosed:true };
  }
  // Portable build routes own the complete scene transaction. Do not first auto-spawn
  // the route case through the ordinary navigation path and then import the build on top of
  // that partially initialized workbench. That double path was harmless for case-only URLs,
  // but could leave a valid /db -> /3d token with only the chassis restored.
  const bridgeRoute=parse3dBuildRoute(window.location.pathname);
  let bridgeHandled=false;
  if(bridgeRoute.stateToken){
    const payload=decodeSffBuildToken(bridgeRoute.stateToken);
    if(payload){
      try{
        const resolver=id=>{
          const key=String(id||'').trim().toLowerCase();
          return AMBIGUOUS_CATALOG_IDS.has(key)?null:(CATALOG_BY_ID.get(key)||null);
        };
        let result=simulatorController?.importSffBuildBridgePayload?.(payload,resolver);
        // A route slug is deliberately retained alongside the immutable catalog ID. If an old
        // build token contains a now-stale case ID, recover the human-readable route case first
        // and retry only the hardware/mode portion. This never fuzzy-resolves hardware IDs.
        if(!result?.ok && result?.reason==='bridge_case_unresolved' && bridgeRoute.slug){
          // This is startup recovery for the current tokenized route, not user navigation.
          // Keep the token until the portable import has had a chance to complete.
          caseNavigation?.addCaseToMat?.(bridgeRoute.slug,{syncRoute:false});
          if(simulatorController?.activeCaseBox?.()){
            result=simulatorController?.importSffBuildBridgePayload?.({...payload,case:null},resolver) || result;
          }
        }
        window.lastSffBuildBridgeImport=result||null;
        bridgeHandled=!!result?.ok;
        console.info('[SFF 3D] Portable build bridge import',result||{ok:false,reason:'import_unavailable'});
        if(result?.missing?.length)console.warn('[SFF 3D] Portable build imported with unresolved records',result.missing);
      }catch(error){
        bridgeHandled=false;
        window.lastSffBuildBridgeImport={ok:false,reason:'bridge_import_exception',error:String(error?.message||error)};
        console.error('[SFF 3D] Portable build bridge import failed; falling back to route case.',error);
      }
    }else{
      console.warn('[SFF 3D] Ignored invalid/unsupported portable build token.');
    }
  }
  // Ordinary case routes still use the normal navigation importer. If a portable token is
  // invalid or its case cannot resolve, fall back to the human-readable route slug instead
  // of leaving an empty scene.
  if(!bridgeHandled)caseNavigation?.checkUrlQueryParamsForCase?.();
}
